import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  Copy,
  Plus,
  Send,
} from "lucide-react";
import {
  ApiError,
  apiFetch,
  createTeamInvite,
  getAdminTeams,
  getTeamInvites,
  resendTeamInvite,
  revokeTeamInvite,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import { unscopedKey } from "@/lib/queryClient";
import { useMe } from "@/features/auth/useMe";
import type { Player, TeamInviteRow } from "@/lib/types";
import { AdminIconButton } from "../components/AdminIconButton";
import { AdminPageHeader } from "../components/AdminPageHeader";
import { AdminUsersPage } from "./UsersPage";

/**
 * M8b E1–E8 — `/admin/people` (membership-scoped people console).
 *
 * Capability gate: page hidden unless the active scope has the
 * `membership:manage` capability OR the user is a global admin
 * (the M8a foundation per-section gate prevents direct nav too).
 *
 * Layout: global admins see the account directory first; each account
 * row owns team status and assignment. Pending/no-account invites are
 * row-owned too: the panel loads every team the signed-in admin can
 * manage, each invite row carries `team_id`, and resend/revoke use
 * that row's team rather than a page-level scope switcher.
 */

const INVITE_ROLES = [
  { value: "team_admin", label: "Team admin" },
  { value: "coach", label: "Coach" },
  { value: "assistant_coach", label: "Assistant coach" },
  { value: "player", label: "Player" },
  { value: "guardian", label: "Guardian" },
];

type InviteTeamOption = { id: string; name: string };

function normalizeInviteTeams<T extends InviteTeamOption>(
  teams: T[],
): InviteTeamOption[] {
  const byId = new Map<string, InviteTeamOption>();
  for (const team of teams) byId.set(team.id, { id: team.id, name: team.name });
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function AdminPeoplePage() {
  const me = useMe().data;
  const isGlobalAdmin = !!me?.user?.is_global_admin;
  const adminTeamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams"),
    queryFn: getAdminTeams,
    enabled: isGlobalAdmin,
    staleTime: 60_000,
  });
  const manageableTeams = useMemo(
    () =>
      isGlobalAdmin
        ? normalizeInviteTeams(adminTeamsQuery.data ?? [])
        : normalizeInviteTeams(
            (me?.teams ?? []).filter((team) =>
              team.capabilities.includes("membership:manage"),
            ),
          ),
    [adminTeamsQuery.data, isGlobalAdmin, me?.teams],
  );
  const canManage = !!(isGlobalAdmin || manageableTeams.length > 0);

  const pageHeader = (
    <AdminPageHeader
      eyebrow="Tenants"
      title="People"
      description="Manage account identities, platform roles, team assignments, invite status, and linked player access in one directory."
    />
  );

  if (!canManage) {
    return (
      <div data-testid="admin-people" className="p-4">
        <p data-testid="admin-people-denied" className="text-destructive text-sm">
          You don't have permission to manage people on this team.
        </p>
      </div>
    );
  }

  const pendingInvites = (
    <section
      id="admin-people-pending-invites"
      data-testid="admin-people-pending-invites"
      className="flex flex-col gap-3"
    >
      <header className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
        <div className="min-w-0">
          <h3 className="text-base font-display uppercase tracking-wider text-ink">
            Pending invites
          </h3>
          <p className="mt-1 text-sm text-ink-muted">
            Invite people who do not have accounts yet. Existing accounts are assigned
            directly from the account row above.
          </p>
        </div>
      </header>

      <InvitesPanel
        teams={manageableTeams}
        teamsLoading={isGlobalAdmin && adminTeamsQuery.isLoading}
      />
    </section>
  );

  return (
    <div data-testid="admin-people" className="flex flex-col gap-4">
      {pageHeader}
      {isGlobalAdmin ? <AdminUsersPage embedded /> : null}
      {pendingInvites}
    </div>
  );
}

// ---------------------------------------------------------------------------
// E5 / E6 / E7 — Invites panel
// ---------------------------------------------------------------------------

function InvitesPanel({
  teams,
  teamsLoading,
}: {
  teams: InviteTeamOption[];
  teamsLoading: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const teamIdsKey = useMemo(
    () => teams.map((team) => team.id).sort().join("|"),
    [teams],
  );
  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const team of teams) map.set(team.id, team.name);
    return map;
  }, [teams]);
  const query = useQuery({
    queryKey: unscopedKey("admin-people-invites", { teamIds: teamIdsKey }),
    queryFn: async () => {
      const perTeam = await Promise.all(
        teams.map((team) => getTeamInvites(team.id)),
      );
      return perTeam.flat().sort((a, b) => {
        const aCreated = Number(a.created_at ?? 0);
        const bCreated = Number(b.created_at ?? 0);
        if (aCreated !== bCreated) return bCreated - aCreated;
        return b.id.localeCompare(a.id);
      });
    },
    enabled: teams.length > 0,
  });
  const [composerOpen, setComposerOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<TeamInviteRow | null>(null);
  // E7 — dev-token surface: stash rows that came back from a
  // create/resend with `invite_token` so the operator can copy them.
  // We hold the full row optimistically too — if a fresh create
  // hasn't been picked up by the list refetch yet (the row is
  // brand-new), the optimistic copy keeps the dev-link visible
  // until the GET catches up. Keyed by invite id so a refresh
  // overwrites the prior URL.
  const [devTokens, setDevTokens] = useState<Record<string, string>>({});
  const [optimisticRows, setOptimisticRows] = useState<TeamInviteRow[]>([]);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-people-invites"),
    });

  const resend = useMutation({
    mutationFn: (invite: TeamInviteRow) => resendTeamInvite(invite.id, invite.team_id),
    onSuccess: (row) => {
      if (row.invite_token) {
        setDevTokens((m) => ({ ...m, [row.id]: row.invite_token! }));
      }
      toast.success("Invite resent");
      void invalidate();
    },
    onError: (err) => {
      toast.error("Couldn't resend invite", { description: formatErr(err) });
    },
  });

  // Merge optimistic rows + server list, deduped by id (server
  // wins once the GET resolves). Re-review P1 defense-in-depth:
  // filter optimistic rows by the currently manageable team ids so
  // a stale entry from a different tenant can never leak into the
  // rendered list.
  const visibleRows: TeamInviteRow[] = useMemo(() => {
    const fromServer = query.data ?? [];
    const knownIds = new Set(fromServer.map((r) => r.id));
    const allowedTeamIds = new Set(teams.map((team) => team.id));
    const stillPending = optimisticRows.filter(
      (r) => allowedTeamIds.has(r.team_id) && !knownIds.has(r.id),
    );
    return [...stillPending, ...fromServer];
  }, [query.data, optimisticRows, teams]);

  return (
    <section
      data-testid="admin-people-invites"
      className="flex flex-col gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Invites
        </h3>
        <AdminIconButton
          icon={Plus}
          label="Send invite"
          size="md"
          tone="accent"
          onClick={() => setComposerOpen(true)}
          data-testid="admin-people-invite-new"
        />
      </header>

      {query.error ? (
        <p
          data-testid="admin-people-invites-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load invites.
        </p>
      ) : null}

      {visibleRows.length === 0 && !query.isLoading ? (
        <p
          data-testid="admin-people-invites-empty"
          className="text-xs font-mono text-ink-muted"
        >
          {teamsLoading
            ? "Loading teams…"
            : teams.length === 0
              ? "No manageable teams available."
              : "No invites sent yet."}
        </p>
      ) : null}

      {visibleRows.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {visibleRows.map((invite) => (
            <InviteRow
              key={invite.id}
              invite={invite}
              teamName={teamNameById.get(invite.team_id) ?? invite.team_id}
              devTokenUrl={devTokens[invite.id]}
              onResend={() => resend.mutate(invite)}
              onRevoke={() => setRevokeTarget(invite)}
              resendPending={resend.isPending && resend.variables?.id === invite.id}
            />
          ))}
        </ul>
      ) : null}

      {composerOpen ? (
        <InviteComposerModal
          teams={teams}
          teamsLoading={teamsLoading}
          onClose={() => setComposerOpen(false)}
          onSuccess={(row) => {
            if (row.invite_token) {
              setDevTokens((m) => ({ ...m, [row.id]: row.invite_token! }));
            }
            // Optimistic insert — the dev-link surfaces immediately
            // even before the list refetch resolves. Once the GET
            // returns, the server row replaces this entry (matched
            // by id, deduped in `visibleRows`).
            setOptimisticRows((rows) => [row, ...rows.filter((r) => r.id !== row.id)]);
            setComposerOpen(false);
            void invalidate();
          }}
        />
      ) : null}

      {revokeTarget ? (
        <RevokeInviteConfirm
          teamId={revokeTarget.team_id}
          invite={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onSuccess={() => {
            setRevokeTarget(null);
            void invalidate();
          }}
        />
      ) : null}
    </section>
  );
}

function InviteRow({
  invite,
  teamName,
  devTokenUrl,
  onResend,
  onRevoke,
  resendPending,
}: {
  invite: TeamInviteRow;
  teamName: string;
  devTokenUrl: string | undefined;
  onResend: () => void;
  onRevoke: () => void;
  resendPending: boolean;
}) {
  const expired =
    invite.expires_at != null && invite.expires_at * 1000 < Date.now();
  const status = invite.status || (expired ? "expired" : "pending");
  const isPending = status === "pending" && !expired;
  return (
    <li
      data-testid="admin-people-invite-row"
      data-invite-id={invite.id}
      data-invite-status={status}
      className="rounded border border-rule/60 bg-elevated px-2 py-1.5"
    >
      <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center text-xs">
        <div className="min-w-0">
          <div className="text-ink truncate">{invite.email}</div>
          <div className="flex flex-wrap items-center gap-1 text-[0.6rem] font-mono text-ink-muted">
            <span
              data-testid="admin-people-invite-row-team"
              data-team-id={invite.team_id}
              className="rounded bg-surface px-1 py-0.5 text-ink"
            >
              {teamName}
            </span>
            <span>{expiryLabel(invite.expires_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <InviteRolePill role={invite.role} />
          <InviteStatusPill status={status} />
          <DeliveryPill
            status={invite.last_delivery_status ?? null}
            isPending={isPending}
          />
        </div>
        <div className="inline-flex gap-1">
          {isPending ? (
            <AdminIconButton
              icon={Send}
              label={resendPending ? "Resending invite" : "Resend invite"}
              onClick={onResend}
              disabled={resendPending}
              data-testid="admin-people-invite-resend"
            />
          ) : null}
          {isPending ? (
            <AdminIconButton
              icon={Ban}
              label="Revoke invite"
              onClick={onRevoke}
              data-testid="admin-people-invite-revoke"
              tone="destructive"
            />
          ) : null}
        </div>
      </div>
      {/* E7 — dev-token copy-link, rendered ONCE per row on the row
       * that just got a fresh token (never logged, never in the
       * success banner — gated control only). */}
      {devTokenUrl ? (
        <div
          data-testid="admin-people-invite-dev-link"
          data-invite-id={invite.id}
          className="mt-1.5 flex items-center gap-1 text-[0.6rem] font-mono"
        >
          <span className="text-ink-muted">Dev invite URL:</span>
          <code className="text-ink break-all">{devTokenUrl}</code>
          <AdminIconButton
            icon={Copy}
            label="Copy invite link"
            onClick={() =>
              navigator.clipboard?.writeText(buildInviteUrl(devTokenUrl))
            }
            data-testid="admin-people-invite-dev-copy"
          />
        </div>
      ) : null}
    </li>
  );
}

function InviteStatusPill({ status }: { status: string }) {
  const tone =
    status === "pending"
      ? "border-accent text-accent"
      : status === "accepted"
        ? "border-good/40 text-good"
        : status === "revoked"
          ? "border-destructive/40 text-destructive"
          : "border-rule text-ink-muted";
  return (
    <span
      data-testid="admin-people-invite-status-pill"
      data-status={status}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${tone}`}
    >
      {status}
    </span>
  );
}

function InviteRolePill({ role }: { role: string }) {
  const colors: Record<string, string> = {
    team_admin: "border-accent text-accent",
    coach: "border-good/40 text-good",
    assistant_coach: "border-good/40 text-good",
    player: "border-rule text-ink",
    guardian: "border-rule text-ink-muted",
  };
  const tone = colors[role] ?? "border-rule text-ink-muted";
  const label =
    INVITE_ROLES.find((r) => r.value === role)?.label ?? role.replace("_", " ");
  return (
    <span
      data-testid="admin-people-role-pill"
      data-role={role}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${tone}`}
    >
      {label}
    </span>
  );
}

// E5 — Delivery pill enumerates the four legacy `last_delivery_status`
// values: sent / disabled / not_configured / failed.
function DeliveryPill({
  status,
  isPending,
}: {
  status: string | null;
  isPending: boolean;
}) {
  if (!isPending) return null;
  const map: Record<string, { label: string; tone: string }> = {
    sent: { label: "Email sent", tone: "border-good/40 text-good" },
    disabled: { label: "Email off", tone: "border-rule text-ink-muted" },
    not_configured: {
      label: "Email not configured",
      tone: "border-rule text-ink-muted",
    },
    failed: { label: "Send failed", tone: "border-destructive/40 text-destructive" },
  };
  const entry = status ? map[status] : null;
  if (!entry) {
    return (
      <span
        data-testid="admin-people-invite-delivery-pill"
        data-delivery="awaiting"
        className="rounded border border-rule bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-ink-muted"
      >
        awaiting
      </span>
    );
  }
  return (
    <span
      data-testid="admin-people-invite-delivery-pill"
      data-delivery={status}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${entry.tone}`}
    >
      {entry.label}
    </span>
  );
}

function expiryLabel(expiresAt: number | null | undefined): string {
  if (!expiresAt) return "no expiry on file";
  const ms = expiresAt * 1000;
  const diff = ms - Date.now();
  if (diff <= 0) return "expired";
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days >= 1) return `expires in ${days}d`;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours >= 1) return `expires in ${hours}h`;
  return "expires in <1h";
}

function buildInviteUrl(token: string): string {
  // The Tktic SPA's invite-accept landing is `/invite/{token}` (a
  // route registered in router.tsx in M9). For dev-token copies
  // we surface the same path — the M9 landing handles both the
  // signed-in accept and the create-account branches.
  if (typeof window === "undefined") return `/invite/${token}`;
  return `${window.location.origin}/invite/${token}`;
}

function InviteComposerModal({
  teams,
  teamsLoading,
  onClose,
  onSuccess,
}: {
  teams: InviteTeamOption[];
  teamsLoading: boolean;
  onClose: () => void;
  onSuccess: (row: TeamInviteRow) => void;
}) {
  const toast = useToast();
  const teamOptions = useMemo(
    () => [
      ...(teams.length > 1
        ? [{ value: "", label: "Pick a team", disabled: true }]
        : []),
      ...teams.map((team) => ({
        value: team.id,
        label: team.name,
      })),
    ],
    [teams],
  );
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("coach");
  const [selectedTeamId, setSelectedTeamId] = useState(
    teams.length === 1 ? teams[0]?.id ?? "" : "",
  );
  const [playerIds, setPlayerIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (teams.length === 1) {
      setSelectedTeamId(teams[0]?.id ?? "");
      return;
    }
    if (
      selectedTeamId &&
      !teams.some((team) => team.id === selectedTeamId)
    ) {
      setSelectedTeamId("");
    }
  }, [selectedTeamId, teams]);

  // Roster is fetched only when role is player/guardian and the modal
  // has a concrete selected team. This keeps player linking tied to
  // the invite's row-owned team rather than the admin shell scope.
  const rosterEnabled =
    !!selectedTeamId && (role === "player" || role === "guardian");
  const rosterQuery = useQuery({
    queryKey: unscopedKey("admin-people-invite-roster", {
      teamId: selectedTeamId || "none",
    }),
    queryFn: () =>
      apiFetch<{ players: Player[] }>("/api/coach/players", {
        scope: { teamId: selectedTeamId, seasonId: null },
      }),
    enabled: rosterEnabled,
  });
  const players: Player[] = rosterEnabled
    ? rosterQuery.data?.players ?? []
    : [];

  const mut = useMutation({
    mutationFn: () =>
      createTeamInvite({
        team_id: selectedTeamId,
        email,
        role,
        player_ids:
          rosterEnabled ? playerIds : undefined,
      }),
    onSuccess: (row) => {
      toast.success("Invite sent");
      onSuccess(row);
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't send invite", { description: formatErr(err) });
    },
  });

  return (
    <ConfirmDialog
      title="Send invite"
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Team
            </span>
            <TkticSelect
              value={selectedTeamId}
              onChange={(next) => {
                setSelectedTeamId(next);
                setPlayerIds([]);
              }}
              options={teamOptions}
              disabled={teamsLoading || teams.length <= 1}
              placeholder={teamsLoading ? "Loading teams…" : "Pick a team"}
              ariaLabel="Invite team"
              size="sm"
              data-testid="admin-people-invite-team"
            />
          </label>

          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-people-invite-email"
              required
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>

          <div
            role="group"
            aria-label="Role"
            data-testid="admin-people-invite-role"
            className="flex flex-col gap-1"
          >
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Role
            </span>
            <div className="flex flex-wrap gap-1">
              {INVITE_ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => {
                    setRole(r.value);
                    if (r.value !== "player" && r.value !== "guardian") {
                      setPlayerIds([]);
                    }
                  }}
                  data-testid={`admin-people-invite-role-${r.value}`}
                  data-active={role === r.value ? "true" : undefined}
                  className="rounded border px-3 py-1 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {rosterEnabled ? (
            <div className="flex flex-col gap-1">
              <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                Link to players
              </span>
              {players.length === 0 ? (
                <p className="text-[0.6rem] font-mono text-ink-muted">
                  {rosterQuery.isLoading
                    ? "Loading roster…"
                    : "No players on roster yet."}
                </p>
              ) : (
                <div
                  data-testid="admin-people-invite-player-list"
                  className="flex flex-col gap-0.5 max-h-40 overflow-auto rounded border border-rule bg-elevated p-1"
                >
                  {players.map((p) => {
                    const selected = playerIds.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setPlayerIds((ids) =>
                            selected
                              ? ids.filter((x) => x !== p.id)
                              : [...ids, p.id],
                          );
                        }}
                        data-testid={`admin-people-invite-player-${p.id}`}
                        data-active={selected ? "true" : undefined}
                        className="flex items-center gap-1 w-full rounded px-1 py-0.5 text-xs text-left transition-colors hover:bg-field data-[active=true]:text-accent"
                      >
                        <span className="text-ink">
                          {p.display_name || p.id}
                          {p.jersey_number ? (
                            <span className="text-ink-muted">
                              {" "}
                              #{p.jersey_number}
                            </span>
                          ) : null}
                        </span>
                        {selected ? <span className="ml-auto text-accent">✓</span> : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : !selectedTeamId && (role === "player" || role === "guardian") ? (
            <p
              data-testid="admin-people-invite-player-scope-note"
              className="rounded border border-rule bg-elevated px-2 py-1 text-[0.6rem] font-mono text-ink-muted"
            >
              Pick a team before linking players.
            </p>
          ) : null}

          {error ? (
            <p
              data-testid="admin-people-invite-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Sending…" : "Send invite"}
      onConfirm={() => {
        setError(null);
        if (!selectedTeamId) {
          setError("Pick a team.");
          return;
        }
        if (!email.trim() || email.length < 3) {
          setError("Email is required.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-people-invite-composer"
    />
  );
}

function RevokeInviteConfirm({
  teamId,
  invite,
  onClose,
  onSuccess,
}: {
  teamId: string;
  invite: TeamInviteRow;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => revokeTeamInvite(invite.id, teamId),
    onSuccess: () => {
      toast.success("Invite revoked");
      onSuccess();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't revoke invite", { description: formatErr(err) });
    },
  });
  return (
    <ConfirmDialog
      title="Revoke invite?"
      message={
        <>
          <p>
            The invite to <strong>{invite.email}</strong> will be cancelled.
            Any pending link becomes invalid.
          </p>
          {error ? (
            <p
              data-testid="admin-people-invite-revoke-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={mut.isPending ? "Revoking…" : "Revoke invite"}
      danger
      onConfirm={() => {
        setError(null);
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-people-invite-revoke-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}
